<template>
  <details class="c2c-history" :open="open" @toggle="onToggle">
    <summary class="c2c-history-summary">
      <Clock :size="16" />
      <span>{{ t('history.heading') }}</span>
      <span v-if="loaded && items.length > 0" class="c2c-history-count">
        {{ items.length }}
      </span>
      <span v-else-if="loaded && items.length === 0" class="c2c-history-empty-tag">
        {{ t('history.empty_short') }}
      </span>
    </summary>

    <div class="c2c-history-body">
      <!-- Both controls sit at the top: at the bottom they were below a
           scrolling list and effectively invisible. -->
      <div v-if="loaded && items.length > 0" class="c2c-history-tools">
        <!-- Both of these used to be bare blue text, which reads as prose
             rather than as something to press. Same button shell, same
             border radius as the rest of the form. -->
        <a
          :href="myUploadsUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="c2c-history-tool"
        >
          <ExternalLink :size="15" aria-hidden="true" />
          <span>{{ t('history.see_all_mine') }}</span>
        </a>
        <button
          type="button"
          class="c2c-history-tool"
          @click="load(true)"
          :disabled="loading"
        >
          <RefreshCw :size="15" aria-hidden="true" :class="{ 'c2c-history-spin': loading }" />
          <span>{{ loading ? t('history.refreshing') : t('history.refresh') }}</span>
        </button>
      </div>

      <p v-if="loadError" class="c2c-history-error">{{ loadError }}</p>

      <p v-else-if="!loaded && loading" class="c2c-history-status">
        {{ t('history.loading') }}
      </p>

      <p v-else-if="loaded && items.length === 0" class="c2c-history-status">
        {{ t('history.empty_full') }}
      </p>

      <ul v-else class="c2c-history-list" @scroll="onListScroll">
        <li v-for="item in visibleItems" :key="item.title" class="c2c-history-item">
          <a
            :href="item.pageUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="c2c-history-link"
            :title="item.title"
          >
            <span class="c2c-history-thumb" v-if="item.thumbUrl">
              <img :src="item.thumbUrl" :alt="''" loading="lazy" />
            </span>
            <span class="c2c-history-thumb c2c-history-thumb--placeholder" v-else aria-hidden="true">
              <Play :size="20" />
            </span>
            <span class="c2c-history-meta">
              <span class="c2c-history-name">{{ shortName(item.title) }}</span>
              <span class="c2c-history-sub">
                <span>{{ formatBytes(item.size) }}</span>
                <template v-if="item.duration != null">
                  <span aria-hidden="true">·</span>
                  <span>{{ formatDuration(item.duration) }}</span>
                </template>
                <span aria-hidden="true">·</span>
                <span>{{ relativeTime(item.timestamp) }}</span>
              </span>
            </span>
            <ExternalLink :size="14" class="c2c-history-arrow" />
          </a>
        </li>
      </ul>

      <button
        v-if="loaded && hasMore"
        type="button"
        class="c2c-history-more"
        @click="revealMore"
      >{{ t('history.show_more', { n: remainingCount }) }}</button>
    </div>
  </details>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { Clock, ExternalLink, Play, RefreshCw } from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import { listUserUploads, type UserUpload } from "@/lib/commons-api"

const { t } = useI18n()

const props = defineProps<{ username: string }>()

const items = ref<UserUpload[]>([])
const loaded = ref(false)
const loading = ref(false)
const loadError = ref<string | null>(null)
const open = ref(false)

/** The contributor's own uploads on Commons. The old link pointed at the
 *  clip2commons tracking category, which is about the tool, not about them. */
const myUploadsUrl = computed(() =>
  "https://commons.wikimedia.org/wiki/Special:ListFiles/" +
  encodeURIComponent(props.username))

/** Rows revealed per step. Fifteen thumbnails at once meant fifteen image
 *  requests before the list settled; five at a time keeps it smooth and
 *  only fetches thumbnails the contributor actually scrolls to. */
const CHUNK = 5
const visibleCount = ref(CHUNK)

const visibleItems = computed(() => items.value.slice(0, visibleCount.value))
const remainingCount = computed(() => Math.max(0, items.value.length - visibleCount.value))
const hasMore = computed(() => remainingCount.value > 0)

function revealMore() {
  visibleCount.value = Math.min(visibleCount.value + CHUNK, items.value.length)
}

/** Reveal the next chunk as the list is scrolled, so the button is a
 *  fallback rather than a toll gate. */
function onListScroll(e: Event) {
  if (!hasMore.value) return
  const el = e.target as HTMLElement
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) revealMore()
}

async function load(force = false) {
  if (!props.username) return
  if (loading.value) return
  if (loaded.value && !force) return
  loading.value = true
  loadError.value = null
  try {
    const list = await listUserUploads(props.username, 15)
    items.value = list
    visibleCount.value = CHUNK
    loaded.value = true
  } catch (err) {
    loadError.value = err instanceof Error ? err.message : String(err)
  } finally {
    loading.value = false
  }
}

function onToggle(e: Event) {
  const detailsEl = e.target as HTMLDetailsElement
  open.value = detailsEl.open
  // Lazy-load on first expand. The <details> element handles open/close;
  // we just kick off the API call when it first opens.
  if (open.value && !loaded.value) load()
}

// If username arrives after mount, lazy-load only stays lazy - we won't
// preemptively call the API. But we do reset state if the user changes
// (logout / re-login as a different account).
watch(() => props.username, (cur, prev) => {
  if (cur !== prev) {
    items.value = []
    loaded.value = false
    loadError.value = null
    if (open.value) load()
  }
})

function shortName(title: string): string {
  return title.replace(/^File:/, "")
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return "0 B"
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB"
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB"
}

function relativeTime(ts: string): string {
  const then = Date.parse(ts)
  if (!Number.isFinite(then)) return ts
  const seconds = Math.floor((Date.now() - then) / 1000)
  if (seconds < 60) return t('history.rel_just_now')
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return t('history.rel_minutes', { n: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('history.rel_hours', { n: hours })
  const days = Math.floor(hours / 24)
  if (days < 30) return t('history.rel_days', { n: days })
  const months = Math.floor(days / 30)
  if (months < 12) return t('history.rel_months', { n: months })
  return t('history.rel_years', { n: Math.floor(months / 12) })
}

function formatDuration(seconds: number): string {
  const s = Math.round(seconds)
  if (s < 0) return "0s"
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (h > 0) return m > 0 ? h + "h " + m + "m" : h + "h"
  if (m > 0) return sec > 0 ? m + "m " + sec + "s" : m + "m"
  return sec + "s"
}
</script>

<style scoped>
.c2c-history {
  border: 1px solid #c8ccd1;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 12px;
}
.c2c-history-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #202122;
  user-select: none;
  list-style: none;
}
.c2c-history-summary::-webkit-details-marker { display: none; }
.c2c-history-summary > span:nth-child(2) { flex: 1 1 auto; }
.c2c-history-summary :deep(svg) { color: #54595d; }

.c2c-history-count {
  background: #eaf3ff;
  color: #2a4b8d;
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
}
.c2c-history-empty-tag {
  font-size: 11px;
  color: #72777d;
  font-weight: 400;
}

.c2c-history-body {
  padding: 0 14px 12px 14px;
  border-top: 1px solid #eaecf0;
}

.c2c-history-status,
.c2c-history-error {
  font-size: 12px;
  margin: 12px 0 4px 0;
}
.c2c-history-error { color: #b32424; }
.c2c-history-status { color: #54595d; }

/* Scrolls inside itself and is sized against the viewport, so opening the
   list never pushes the upload form onto a second screen. */
.c2c-history-list {
  list-style: none;
  margin: 6px 0 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  /* Sized so the card plus the dropzone below it still land inside one
     screen on a laptop. 40vh of list pushed the thing people came to use
     below the fold. */
  max-height: min(30vh, 268px);
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
}

/*
 * Short viewports: a phone in landscape, or a small laptop. This list is
 * the one thing on the first screen that can afford to give up height, so
 * it is what shrinks rather than the upload form below it. The rule lives
 * here rather than in App.vue because the base rule above is scoped, and a
 * plain global selector loses to it on specificity.
 */
@media (max-height: 640px) {
  .c2c-history-list { max-height: 34vh; }
}

.c2c-history-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0 10px 0;
}
.c2c-history-tool {
  display: inline-flex; align-items: center; gap: 6px;
  min-height: 34px; padding: 6px 12px;
  font: inherit; font-size: 13px; font-weight: 600;
  color: #202122; text-decoration: none;
  background: #fff;
  border: 1px solid var(--border-color-base, #a2a9b1);
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 120ms ease-out, border-color 120ms ease-out;
}
.c2c-history-tool:hover { background: #eaecf0; text-decoration: none; }
.c2c-history-tool:focus-visible {
  outline: 2px solid transparent;
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}
.c2c-history-tool:disabled { opacity: 0.6; cursor: wait; }
.c2c-history-tool :deep(svg) { flex: 0 0 auto; color: #54595d; }
@keyframes c2c-history-spin { to { transform: rotate(360deg); } }
.c2c-history-spin { animation: c2c-history-spin 0.9s linear infinite; }
@media (prefers-reduced-motion: reduce) {
  .c2c-history-spin { animation: none; }
}

.c2c-history-more {
  margin-top: 8px;
  width: 100%;
  background: #fff;
  border: 1px solid var(--border-color-base, #a2a9b1);
  border-radius: 6px;
  padding: 7px 12px;
  min-height: 32px;
  font: inherit;
  font-size: 12px;
  color: #202122;
  cursor: pointer;
}
.c2c-history-more:hover { background: var(--background-color-neutral, #eaecf0); }

.c2c-history-item {
  border-radius: 4px;
  transition: background 0.1s;
}
.c2c-history-item:hover { background: #f8f9fa; }

.c2c-history-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 6px;
  text-decoration: none;
  color: inherit;
  border-radius: 4px;
}
.c2c-history-link:focus-visible {
  outline: 2px solid #3366cc;
  outline-offset: 0;
}

.c2c-history-thumb {
  flex-shrink: 0;
  width: 48px;
  height: 36px;
  background: #1e1e1e;
  border-radius: 3px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.c2c-history-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.c2c-history-thumb--placeholder :deep(svg) {
  width: 16px;
  height: 16px;
  color: #fff;
  opacity: 0.7;
}

.c2c-history-meta {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.c2c-history-name {
  font-size: 14px;
  font-weight: 500;
  color: #202122;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.c2c-history-sub {
  font-size: 12.5px;
  color: #54595d;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}
.c2c-history-sub > span:nth-child(2) { color: #a2a9b1; }

.c2c-history-arrow {
  flex-shrink: 0;
  color: #72777d;
}

</style>
